"use client";

import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Download, FileText } from "lucide-react";
import { Footer } from "@/components/sections/premium/ManifestoSection";
import { caseStudies } from "@/components/sections/premium/caseStudiesData";
import { noteForCase } from "@/components/sections/premium/testimonialsData";
import { WhatsAppNote } from "@/components/ui/WhatsAppNote";

const openPdf = (slug?: string) => {
  const url = slug ? `/case-studies/print?id=${slug}` : "/case-studies/print";
  window.open(url, "_blank");
};

export const CaseStudiesContent = () => {
  return (
    <main className="relative bg-[#0a0a0a] text-[#f4f4f0] min-h-screen selection:bg-primary selection:text-white font-sans overflow-hidden">
      {/* COLLINS STYLE HERO */}
      <section className="relative h-screen w-full flex flex-col justify-between p-6 md:p-12 overflow-hidden bg-black">
        <div className="absolute inset-0 z-0">
          <video
            src="/case-studies.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-40 grayscale mix-blend-screen scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a]/50 to-[#0a0a0a]" />
        </div>
        
        <div className="relative z-10 flex justify-between items-start mt-24">
          <motion.span 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="font-sans text-sm md:text-base uppercase tracking-[0.4em] text-white/50"
          >
            DOTSLASHLAB / PORTFOLIO
          </motion.span>
          <motion.span 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="font-mono text-xs md:text-sm text-white/30 text-right tracking-widest"
          >
            PROVEN RECORD <br/>
            OF EXECUTION
          </motion.span>
        </div>

        <div className="relative z-10 w-full flex justify-center items-center h-full">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col items-center"
          >
            <h1 className="font-display text-[16vw] md:text-[18vw] leading-[0.75] tracking-tight uppercase text-center">
              CASE <br />
              <span className="text-transparent italic" style={{ WebkitTextStroke: '2px #f4f4f0' }}>STUDIES.</span>
            </h1>
            <button
              onClick={() => openPdf()}
              className="mt-12 inline-flex items-center gap-3 bg-[#f4f4f0] text-[#0a0a0a] hover:bg-primary hover:text-white px-8 py-4 rounded-full font-mono text-[10px] uppercase tracking-[0.3em] transition-colors duration-500"
            >
              <FileText className="w-4 h-4" /> Download Full Portfolio (PDF)
            </button>
          </motion.div>
        </div>
      </section>

      {/* CASE STUDIES EDITORIAL LIST */}
      <section className="relative w-full z-10 bg-[#0a0a0a]">
        {caseStudies.map((study, index) => {
          return (
            <div key={study.id} className="relative w-full min-h-screen flex items-center justify-center py-32 border-b border-white/5 group">
              <div className="max-w-[1600px] mx-auto w-full px-6 md:px-12 relative z-10">
                
                {/* MASSIVE BACKGROUND ID */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[40vw] font-display text-white/[0.015] leading-none pointer-events-none select-none text-center w-full">
                  {study.id}
                </div>

                {/* HEADER: BADGE + TITLE (full width), then OVERVIEW (left) + STACK/DOWNLOAD (right) */}
                <div className="relative mb-16 lg:mb-24">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-10 h-10 rounded-full border border-primary flex justify-center items-center text-primary font-mono text-xs">
                      {study.id}
                    </div>
                    <span className="font-mono text-[10px] tracking-widest uppercase text-white/50">{study.category}</span>
                  </div>
                  <h2 className="font-display text-[14vw] md:text-[12vw] lg:text-[9vw] leading-[0.85] tracking-tight uppercase text-white group-hover:text-primary transition-colors duration-700">
                    {study.title}
                  </h2>

                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mt-12 items-start">
                    <p className="lg:col-span-7 font-sans text-lg md:text-2xl leading-relaxed text-white/60">
                      {study.overview}
                    </p>
                    <div className="lg:col-span-5 lg:pl-4">
                      <div className="flex flex-wrap gap-2">
                        {study.techStack.map(tech => (
                          <span key={tech} className="font-mono text-[10px] uppercase tracking-[0.2em] border border-white/10 px-5 py-3 rounded-full text-white/70 hover:bg-white hover:text-black transition-colors duration-500 cursor-default">
                            {tech}
                          </span>
                        ))}
                      </div>
                      <button
                        onClick={() => openPdf(study.slug)}
                        className="mt-8 inline-flex items-center gap-3 border border-white/20 hover:border-primary hover:text-primary px-7 py-4 rounded-full font-mono text-[10px] uppercase tracking-[0.3em] text-white/80 transition-colors duration-500"
                      >
                        <Download className="w-4 h-4" /> Download Case Study (PDF)
                      </button>
                    </div>
                  </div>
                </div>

                {/* CHALLENGE + SOLUTION: SIDE BY SIDE, FULL WIDTH */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-20 lg:gap-x-20 relative">
                  {/* CHALLENGE */}
                  <div className="space-y-10">
                    <div>
                      <span className="font-mono text-xs text-primary uppercase tracking-[0.3em] block mb-6">/ The Challenge</span>
                      <h3 className="font-sans text-2xl md:text-4xl leading-tight text-white/90 font-light italic">
                        &ldquo;{study.challengePrefix}&rdquo;
                      </h3>
                    </div>
                    {study.challenge.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
                        {study.challenge.map((c, i) => (
                          <div key={i} className="border-t border-white/10 pt-6">
                            <span className="font-display text-3xl text-white/20 mb-4 block">{(i+1).toString().padStart(2, '0')}</span>
                            <p className="font-sans text-lg text-white/70 leading-relaxed">{c}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* SOLUTION */}
                  <div className="space-y-10 lg:border-l lg:border-white/10 lg:pl-12 xl:pl-20">
                    <div>
                      <span className="font-mono text-xs text-primary uppercase tracking-[0.3em] block mb-6">/ The Solution</span>
                      <h3 className="font-sans text-2xl md:text-4xl leading-tight text-white/90 font-light">
                        {study.solution}
                      </h3>
                    </div>
                    {study.solutionList.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
                        {study.solutionList.map((s, i) => (
                          <div key={i} className="border-t border-white/10 pt-6">
                            <span className="font-display text-3xl text-white/20 mb-4 block">{(i+1).toString().padStart(2, '0')}</span>
                            <p className="font-sans text-lg text-white/70 leading-relaxed">{s}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* OUTCOMES - FULL WIDTH HIGH CONTRAST INVERTED BLOCK */}
                <div className="mt-24 lg:mt-32 bg-[#f4f4f0] text-[#0a0a0a] p-8 md:p-16 lg:p-24 rounded-[2rem] shadow-2xl relative z-20">
                  <span className="font-mono text-xs text-black/40 uppercase tracking-[0.3em] block mb-12">/ Outcomes</span>
                  <div className={`grid grid-cols-1 md:grid-cols-2 ${study.outcomes.length % 3 === 0 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-12 lg:gap-16`}>
                    {study.outcomes.map((outcome, i) => (
                      <div key={i} className="flex flex-col border-t border-black/10 pt-8 group/outcome">
                        <span className="font-display text-7xl md:text-8xl text-black/10 leading-none group-hover/outcome:text-primary transition-colors duration-500 mb-6">
                          0{i+1}
                        </span>
                        <p className="font-sans text-xl md:text-2xl uppercase tracking-tight leading-tight text-black/90">
                          {outcome}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* A NOTE FROM THE CLIENT — WHATSAPP STYLE */}
                {(() => {
                  const note = noteForCase(study.slug);
                  if (!note) return null;
                  return (
                    <div className="mt-20 lg:mt-28">
                      <span className="font-mono text-xs text-primary uppercase tracking-[0.3em] block mb-8">/ A note from the client</span>
                      <WhatsAppNote name={note.name} message={note.note!} time={note.noteTime} reply={note.reply} replyTime={note.replyTime} />
                    </div>
                  );
                })()}

              </div>
            </div>
          );
        })}
      </section>

      {/* FOOTER */}
      <Footer />

      <div className="bg-noise opacity-10 pointer-events-none fixed inset-0 z-50 mix-blend-overlay" />
    </main>
  );
};
